import React from "react";
import { Slider } from "antd";
import { Container } from "../HtmlTags/Container/Container";
import { Span } from "../HtmlTags/Span/Span";
import "./DistanceRangeSlider.css";

interface DistanceRangeSliderProps {
  min: number;
  max: number;
  value: [number, number] | number;
  onChange: (value: [number, number] | number) => void;
  leftLabel?: string;
  rightLabel?: string;
  className?: string;
  singleValue?: boolean;
}

export const DistanceRangeSlider: React.FC<DistanceRangeSliderProps> = ({
  min,
  max,
  value,
  onChange,
  leftLabel = `<${min}km`,
  rightLabel = `${max}km+`,
  className = "",
  singleValue = false,
}) => {
  const displayValue = singleValue ? (value as number) : (value as [number, number])[1];

  if (singleValue) {
    return (
      <Container
        className={`distance-range-slider-container ${className}`}
        data-testid="distance-range-slider"
      >
        <Slider
          min={min}
          max={max}
          value={value as number}
          onChange={(val) => {
            const numVal = Array.isArray(val) ? val[0] : val;
            onChange(numVal);
          }}
          className="custom-slider"
        />

        <Container className="slider-labels">
          <Span className="label left">{leftLabel}</Span>
          <Span className="label right">{`${displayValue}km`}</Span>
        </Container>
      </Container>
    );
  }

  return (
    <Container className={`distance-range-slider-container ${className}`}>
      <Slider
        range
        min={min}
        max={max}
        value={value as [number, number]}
        onChange={(val) => onChange(val as [number, number])}
        className="custom-slider"
      />

      <Container className="slider-labels">
        <Span className="label left">{leftLabel}</Span>
        <Span className="label right">{rightLabel}</Span>
      </Container>
    </Container>
  );
};

/**
 * DistanceRangeSlider is a reusable component built on Ant Design's Slider
 * that allows users to select a range between a minimum and maximum distance.
 *
 * @component
 *
 * @param {number} min - The minimum value of the slider.
 * @param {number} max - The maximum value of the slider.
 * @param {[number, number]} value - The current selected range as a tuple.
 * @param {(value: [number, number]) => void} onChange - Callback function called when the range changes.
 * @param {string} [leftLabel] - Optional label displayed on the left side of the slider. Defaults to `<{min}km`.
 * @param {string} [rightLabel] - Optional label displayed on the right side of the slider. Defaults to `{max}km+`.
 * @param {React.ReactNode} [centerComponent] - Optional center element between the labels (currently unused).
 *
 * @example
 * <DistanceRangeSlider
 *   min={0}
 *   max={100}
 *   value={[10, 50]}
 *   onChange={(range) => console.log(range)}
 * />
 */
